import { useEffect } from 'react';
import { Button, Form, Grid, Input, Message } from '@arco-design/web-react';
import { PageHeader } from '@/components/ui';
import { authApi } from '@/api';
import { useAuthStore } from '@/store/authStore';
import { EMAIL_REGEX, PHONE_INTL_REGEX } from '@/utils/validators';

const { Row, Col } = Grid;

export default function ProfileSettingsView() {
  const { profile, patchProfile, refreshProfile } = useAuthStore();
  const [contactForm] = Form.useForm<{ email: string; phone: string }>();
  const [pwdForm] = Form.useForm<{
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
  }>();

  useEffect(() => {
    if (profile) {
      contactForm.setFieldsValue({
        email: profile.email ?? '',
        phone: profile.phone ?? '',
      });
    }
  }, [profile, contactForm]);

  const handleContactSubmit = async () => {
    try {
      const v = await contactForm.validate();
      const next = await authApi.updateContact(v);
      patchProfile(next);
      Message.success('联系方式已更新');
    } catch (e) {
      if (e instanceof Error) Message.error(e.message);
    }
  };

  const handlePasswordSubmit = async () => {
    try {
      const v = await pwdForm.validate();
      if (v.newPassword !== v.confirmPassword) {
        Message.error('两次输入的新密码不一致');
        return;
      }
      await authApi.changePassword({
        oldPassword: v.oldPassword,
        newPassword: v.newPassword,
      });
      Message.success('密码已更新，请使用新密码重新登录');
      pwdForm.resetFields();
      refreshProfile();
    } catch (e) {
      if (e instanceof Error) Message.error(e.message);
    }
  };

  return (
    <div className="up-page">
      <PageHeader title="个人设置中心" subtitle="维护登录凭证与基本联系方式" />
      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <div className="up-section-card">
            <div className="up-page__title" style={{ fontSize: 16, marginBottom: 12 }}>
              基本联系信息
            </div>
            <Form form={contactForm} layout="vertical">
              <Form.Item label="登录账号">
                <Input value={profile?.username} disabled />
              </Form.Item>
              <Form.Item
                label="主要邮箱"
                field="email"
                rules={[
                  { required: true, message: '邮箱必填' },
                  { match: EMAIL_REGEX, message: '邮箱格式不正确' },
                ]}
              >
                <Input placeholder="user@example.com" />
              </Form.Item>
              <Form.Item
                label="通知电话（含国际区号）"
                field="phone"
                rules={[{ match: PHONE_INTL_REGEX, message: '请使用 +区号 开头格式' }]}
              >
                <Input placeholder="+86-138-0000-0000" allowClear />
              </Form.Item>
              <Button type="primary" onClick={handleContactSubmit}>
                保存联系方式
              </Button>
            </Form>
          </div>
        </Col>

        <Col xs={24} lg={12}>
          <div className="up-section-card">
            <div className="up-page__title" style={{ fontSize: 16, marginBottom: 12 }}>
              安全凭证维护
            </div>
            <Form form={pwdForm} layout="vertical">
              <Form.Item
                label="原登录密码"
                field="oldPassword"
                rules={[{ required: true, message: '请输入原密码' }]}
              >
                <Input.Password placeholder="请输入" />
              </Form.Item>
              <Form.Item
                label="新登录密码"
                field="newPassword"
                rules={[
                  { required: true, message: '请输入新密码' },
                  { minLength: 8, message: '至少 8 位' },
                ]}
              >
                <Input.Password placeholder="至少 8 位，含字母与数字" />
              </Form.Item>
              <Form.Item
                label="确认新密码"
                field="confirmPassword"
                rules={[{ required: true, message: '请再次输入' }]}
              >
                <Input.Password placeholder="再次输入新密码" />
              </Form.Item>
              <Button type="primary" status="warning" onClick={handlePasswordSubmit}>
                更新密码
              </Button>
            </Form>
          </div>
        </Col>
      </Row>
    </div>
  );
}
